import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Virtuoso } from 'react-virtuoso';
import {
  NEW_RECOMMEND_FEED_INTERVAL,
  RECOMMEND_FEED_NEW_COUNT_URL,
  useNewRecommendStreamsCount,
  useRecommendFeedList,
  useUpdateStream,
} from '../../hooks';
import { FeedListItem, LoadingSkeleton } from '../FeedListItem';
import { useCurrentUserId } from '@/utils';
import { TABS } from '../../const';
import { useDidRecover } from 'react-router-cache-route';
import { EmptyStream, FeedItem } from '../..';
import { Loading } from '@/components';
import { useTranslation } from 'react-i18next';
import styles from './index.module.scss';
import dayjs from 'dayjs';

type RecommendPostListProps = {};

export type RecommendPostListHandle = {
  refetchFirstPage: () => void;
};

export const RecommendPostList = forwardRef<RecommendPostListHandle, RecommendPostListProps>(
  (props, ref) => {
    const { t } = useTranslation();
    const address = useCurrentUserId();
    const [enabledCount, setEnabledCount] = useState(false);
    const {
      result: recommendFeed,
      isLoading,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      refetchFirstPage,
      isFetching,
    } = useRecommendFeedList({ address });
    const createAtRef = useRef(dayjs().unix());
    const timerRef = useRef<NodeJS.Timeout>();

    const _refetchFirstPage = useCallback(() => {
      setEnabledCount(false);
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        setEnabledCount(true);
      }, NEW_RECOMMEND_FEED_INTERVAL);
      refetchFirstPage(RECOMMEND_FEED_NEW_COUNT_URL);
      createAtRef.current = dayjs().unix();
    }, [refetchFirstPage]);

    const { data: newStreamsCount } = useNewRecommendStreamsCount({
      create_at: createAtRef.current,
      enabled: enabledCount,
    });

    useEffect(() => {
      // 在组件挂载后 10 分钟后启用请求
      timerRef.current = setTimeout(() => {
        setEnabledCount(true);
      }, NEW_RECOMMEND_FEED_INTERVAL);
      return () => clearTimeout(timerRef.current);
    }, []);

    useImperativeHandle(
      ref,
      () => {
        return {
          refetchFirstPage: _refetchFirstPage,
        };
      },
      [_refetchFirstPage],
    );

    const recommendShownList = useMemo(
      () => recommendFeed?.filter(x => !x?.article?.is_withdrawn && x?.article?.is_visible),
      [recommendFeed],
    );

    const { updateFeeds, onRangeChanged } = useUpdateStream(TABS[1].mode, {
      [TABS[1].mode]: recommendShownList,
    });

    const isRecommendEmpty = useMemo(() => !recommendShownList?.length, [recommendShownList]);

    const fetchMore = useCallback(() => {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

    const itemContent = useCallback(
      (index, item) => (
        <FeedListItem
          feed={item}
          restParams={{
            source: 'article',
            article_id: item.article.id,
            contributor_id: item.article._contributorId,
          }}
          enableReadFeedback
          enableContributorId
          openDetailInSelf
          disableBorderBottom={index === (recommendShownList?.length || 0) - 1}
          showHideBtn
        />
      ),
      [recommendShownList?.length],
    );
    const computeItemKey = useCallback((_, item: FeedItem) => item.id, []);

    useDidRecover(() => {
      document.documentElement.scrollTop = 0;
      updateFeeds();
    });

    return (
      <div>
        {Number(newStreamsCount) > 0 ? (
          <div className={styles.newStreamsCount} onClick={() => _refetchFirstPage()}>
            {isFetching ? <Loading /> : t('ListContainer.showNewFeeds', { count: newStreamsCount })}
          </div>
        ) : null}
        {recommendShownList?.length ? (
          <Virtuoso
            useWindowScroll
            data={recommendShownList}
            overscan={10}
            endReached={fetchMore}
            itemContent={itemContent}
            rangeChanged={onRangeChanged.recommend}
            computeItemKey={computeItemKey}
          />
        ) : null}
        {isLoading || isFetchingNextPage ? (
          <LoadingSkeleton />
        ) : isRecommendEmpty ? (
          <EmptyStream placeholder="No results found." />
        ) : null}
      </div>
    );
  },
);
